import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiKey } from '../../../core/models';
import { ApiKeysService } from '../../../core/services';
import { BillingService } from '../../billing/services/billing.service';
import { UsageResult } from '../../billing/models/usage.model';
import { OverviewResult } from '../models/overview.model';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** [from, to] for the N-day window ending today, inclusive. */
function computeRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

/**
 * Backs the Overview (dashboard) page. There is no single `GET /dashboard`
 * endpoint, and this doesn't need one every section already has a real,
 * confirmed source elsewhere in the app:
 *   - wallet balance + recent activity  → GET /wallet           (BillingService)
 *   - KPI tiles + chart + top endpoints → GET /usage (BillingService) one
 *     call; `deltas` on the response is the % change vs. the prior window,
 *     computed server-side and returned unconditionally, so nothing gets
 *     fetched twice or measured on this end
 *   - API keys summary                  → GET /api_keys?status=active (ApiKeysService)
 * Composed client-side with forkJoin instead of waiting on one combined
 * payload same reasoning as the admin Overview split (see AdminService).
 */
@Injectable({ providedIn: 'root' })
export class OverviewService {
  constructor(
    private billingService: BillingService,
    private apiKeysService: ApiKeysService,
  ) {}

  getOverview(days: number): Observable<OverviewResult> {
    const range = computeRange(days);

    return forkJoin({
      wallet: this.billingService.getWallet(),
      usage: this.billingService.getUsage(range),
      apiKeys: this.apiKeysService.getActiveApiKeys(),
    }).pipe(
      map(({ wallet, usage, apiKeys }) => new OverviewResult({
        range,

        wallet: {
          balance_cents: wallet.wallet.balanceCents,
          balance: wallet.wallet.balance,
          currency: wallet.wallet.currency,
        },

        stats: this.buildStats(usage, days),

        // Same single metric (requests/day) the mock chart plotted the
        // KPI tiles below carry the other four metrics' own sparklines.
        series: usage.series.map(p => ({ date: p.date, value: p.requests })),

        // byEndpoint is already sorted by requests desc, server-side.
        top_endpoints: usage.byEndpoint
          .slice(0, 5)
          .map(e => ({ endpoint: e.endpoint, requests: e.requests })),

        api_keys: this.buildApiKeysSummary(apiKeys),

        recent_activity: wallet.recentTransactions.map(t => ({
          id: t.id,
          occurred_at: t.createdAt,
          kind: t.kind,
          description: t.description,
          amount: t.amount,
          currency: t.currency,
          is_credit: t.isCredit,
          // The wallet ledger has no status field of its own an entry
          // only ever appears here once it's settled, so this is always true.
          status: 'completed',
        })),
      })),
    );
  }

  private buildStats(usage: UsageResult, days: number) {
    const t = usage.totals;
    // `deltas` comes back unconditionally on every /usage response kept
    // nullable here anyway so a tile falls back to 0 (flat, badge hidden)
    // rather than crashing if a response ever omits it.
    const d = usage.deltas;
    const caption = days === 365 ? 'last year' : `last ${days} days`;

    return [
      {
        key: 'requests',
        label: 'Requests',
        value: t.requests.toLocaleString('en-US'),
        caption,
        delta_pct: d?.requestsPct ?? 0,
        good_direction: 'up',
        series: usage.series.map(pt => pt.requests),
      },
      {
        key: 'credits',
        label: 'Credits used',
        value: t.creditsUsed.toLocaleString('en-US'),
        caption,
        delta_pct: d?.creditsUsedPct ?? 0,
        good_direction: 'up',
        series: usage.series.map(pt => pt.credits),
      },
      {
        key: 'error_rate',
        label: 'Error rate',
        value: `${(t.errorRate * 100).toFixed(2)}%`,
        caption,
        delta_pct: d?.errorRatePct ?? 0,
        good_direction: 'down',
        series: usage.series.map(pt => Math.round(pt.errorRate * 1000) / 10),
      },
      {
        key: 'avg_latency',
        label: 'Avg latency',
        value: `${Math.round(t.avgLatencyMs)} ms`,
        caption,
        delta_pct: d?.avgLatencyMsPct ?? 0,
        good_direction: 'down',
        series: usage.series.map(pt => pt.avgLatencyMs),
      },
    ];
  }

  private buildApiKeysSummary(apiKeys: ApiKey[]) {
    const mostRecent = apiKeys
      .filter(k => k.lastUsedAt)
      .sort((a, b) => new Date(b.lastUsedAt!).getTime() - new Date(a.lastUsedAt!).getTime())[0];

    return {
      active_count: apiKeys.length,
      most_recently_used: mostRecent
        ? { name_prefix: mostRecent.prefix, used_at: mostRecent.lastUsedAt }
        : null,
    };
  }
}
